import React, { forwardRef, useState } from "react";
import { Users, Clock, Check, Star, MapPin } from "lucide-react";
import { useLanguage } from "../context/LanguageContext";
import { IMAGES, VEHICLE_NAME_KEYS, AIRPORTS, DESTINATIONS } from "../mock";
import { Button } from "./ui/button";
import BookingModal from "./BookingModal";

const featureKeysByTier = {
  van: ["incAC", "incMeet", "incWater", "incFlight"],
  suv: ["incAC", "incMeet", "incWater", "incLead"],
  luxury: ["incVip", "incMeet", "incWater", "incFlight"],
};

const VehicleResults = forwardRef(({ result }, ref) => {
  const { t } = useLanguage();
  const [modalOpen, setModalOpen] = useState(false);
  const [selected, setSelected] = useState(null);

  if (!result || !result.quote) {
    return (
      <section ref={ref} className="max-w-7xl mx-auto px-6 -mt-14 relative z-10">
        <div className="bg-white rc-shadow rounded-2xl p-8 text-center text-slate-500 border border-teal-50">
          {t("completeSearch")}
        </div>
      </section>
    );
  }

  const { quote, tripType } = result;
  const isRound = tripType === "round";
  const origin =
    tripType === "hth"
      ? DESTINATIONS.find((d) => d.id === result.origin)?.name
      : AIRPORTS.find((a) => a.id === result.origin)?.name;

  const handleSelect = (v) => {
    setSelected({
      tripType,
      origin: result.origin,
      destinationId: result.destinationId,
      hotel: result.hotel,
      tier: v.tier,
      arrivalDate: result.arrivalDate,
      departureDate: result.departureDate,
      passengers: result.passengers,
      price: v.price_usd,
      summary: `${origin} → ${quote.destination_name}`,
    });
    setModalOpen(true);
  };

  return (
    <section ref={ref} className="max-w-7xl mx-auto px-6 -mt-14 relative z-10">
      <div className="bg-white rc-shadow-lg rounded-2xl p-6 sm:p-8 border border-teal-50">
        <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
          <div>
            <h2 className="font-display font-extrabold text-2xl text-[#0d3b43]">
              {t("resultsTitle")}
            </h2>
            <p className="text-sm text-slate-500 flex items-center gap-1.5 mt-1">
              <MapPin className="w-4 h-4 text-teal-500" />
              {origin} → {quote.destination_name}
              {result.hotel ? ` · ${result.hotel}` : ""}
            </p>
          </div>
          <span className="text-xs font-bold uppercase tracking-wide bg-teal-50 text-teal-700 px-3 py-1.5 rounded-full">
            {t(isRound ? "perTripRound" : "perTripOne")}
          </span>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {quote.vehicles.map((v) => (
            <div
              key={v.tier}
              className="group border border-teal-100 rounded-2xl overflow-hidden hover:rc-shadow-lg hover:border-teal-300 transition-all duration-300 flex flex-col"
            >
              <div className="relative h-44 overflow-hidden bg-slate-100">
                <img
                  src={IMAGES[v.image_key]}
                  alt={t(VEHICLE_NAME_KEYS[v.tier].name)}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                />
                {v.popular && (
                  <span className="absolute top-3 left-3 flex items-center gap-1 bg-orange-500 text-white text-[11px] font-bold px-2.5 py-1 rounded-full">
                    <Star className="w-3 h-3 fill-white" /> Popular
                  </span>
                )}
                <span className="absolute bottom-3 left-3 flex items-center gap-1.5 bg-white/90 backdrop-blur text-[#0d3b43] text-xs font-bold px-2.5 py-1 rounded-full">
                  <Users className="w-3.5 h-3.5" /> {t("upTo")} {v.capacity} {t("pax")}
                </span>
              </div>

              <div className="p-5 flex flex-col flex-1">
                <h3 className="font-display font-bold text-lg text-[#0d3b43]">
                  {t(VEHICLE_NAME_KEYS[v.tier].name)}
                </h3>
                <p className="text-sm text-slate-500 mt-1">
                  {t(VEHICLE_NAME_KEYS[v.tier].desc)}
                </p>

                <div className="mt-4 space-y-2">
                  {featureKeysByTier[v.tier].map((fk) => (
                    <div
                      key={fk}
                      className="flex items-center gap-2 text-sm text-slate-600"
                    >
                      <Check className="w-4 h-4 text-teal-500 shrink-0" />
                      {t(fk)}
                    </div>
                  ))}
                </div>

                <div className="mt-4 flex items-center gap-1.5 text-xs text-slate-400">
                  <Clock className="w-3.5 h-3.5" />
                  {t("travelTime")} {quote.minutes} {t("min")}
                </div>

                <div className="mt-auto pt-5 flex items-end justify-between">
                  <div>
                    <span className="block text-[11px] text-slate-400 font-medium">
                      {t(isRound ? "perTripRound" : "perTripOne")}
                    </span>
                    <span className="font-display font-extrabold text-2xl text-[#0d3b43]">
                      ${v.price_usd.toLocaleString("en-US")}
                      <span className="text-sm font-semibold text-slate-400"> USD</span>
                    </span>
                  </div>
                  <Button
                    onClick={() => handleSelect(v)}
                    className="bg-teal-600 hover:bg-teal-700 text-white font-bold rounded-full px-5"
                  >
                    {t("selectVehicle")}
                  </Button>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      <BookingModal open={modalOpen} onOpenChange={setModalOpen} booking={selected} />
    </section>
  );
});

VehicleResults.displayName = "VehicleResults";
export default VehicleResults;
